import React from 'react';
import { MapNode, InnerControl } from './useMapLayout';
import { UIControl, ControlType } from '../../../types/persona_engine';

interface Props {
  node: MapNode;
  transform: { x: number; y: number; scale: number };
  canvasWidth: number;
  canvasHeight: number;
  darkMode: boolean;
}

const TYPE_ICONS: Record<string, string> = {
  slider: '━',
  toggle: '◉',
  select: '▾',
  text:   'T',
};

const TYPE_COLORS: Record<string, string> = {
  slider: '#a855f7',
  toggle: '#8b5cf6',
  select: '#7c3aed',
  text:   '#6d28d9',
};

function formatValue(ctrl: UIControl): string {
  const v = ctrl.current_value;
  if (ctrl.type === ControlType.TOGGLE) return v ? 'ON' : 'OFF';
  if (ctrl.type === ControlType.SLIDER) {
    if (typeof v === 'number' && (ctrl.max ?? 1) <= 1) {
      return `${Math.round(v * 100)}%`;
    }
    return typeof v === 'number' ? v.toFixed(2) : String(v);
  }
  if (typeof v === 'string') return v.length > 10 ? v.slice(0, 10) + '…' : v;
  return String(v);
}

/**
 * Full HTML overlay for controls_cluster at high zoom (> LOD_NODES).
 * Groups controls by type column — mirrors the identity_cluster pillar layout.
 * Each control card shows:
 *   - left type-color accent bar
 *   - type icon + label
 *   - slider: fill bar + % value
 *   - toggle: ON/OFF badge
 *   - select/text: truncated value
 *   - amber highlight + dot if non-default
 * pointer-events: none — read-only display.
 */
const ControlsClusterOverlay: React.FC<Props> = ({
  node,
  transform,
  canvasWidth,
  canvasHeight,
  darkMode,
}) => {
  if (!node.innerControls?.length) return null;

  const toScreen = (wx: number, wy: number) => ({
    sx: wx * transform.scale + transform.x,
    sy: wy * transform.scale + transform.y,
  });

  const clusterScreen = toScreen(node.x, node.y);
  const clusterScreenR = node.radius * transform.scale;

  // Viewport cull
  if (
    clusterScreen.sx + clusterScreenR < 0 ||
    clusterScreen.sx - clusterScreenR > canvasWidth ||
    clusterScreen.sy + clusterScreenR < 0 ||
    clusterScreen.sy - clusterScreenR > canvasHeight
  ) return null;

  return (
    <>
      {node.innerControls.map((ic) => {
        const wx = node.x + ic.dx;
        const wy = node.y + ic.dy;
        const { sx, sy } = toScreen(wx, wy);
        const ctrl: UIControl = ic.data;
        const isNonDefault = ctrl.current_value !== ctrl.default_value;
        const typeColor = TYPE_COLORS[ctrl.type] ?? '#a855f7';
        const cardW = Math.max(110, Math.min(200, node.radius * transform.scale * 0.95));

        // Slider fill pct
        const sliderPct = ctrl.type === ControlType.SLIDER && typeof ctrl.current_value === 'number'
          ? Math.max(0, Math.min(1,
              (ctrl.current_value - (ctrl.min ?? 0)) / ((ctrl.max ?? 1) - (ctrl.min ?? 0))
            ))
          : null;

        return (
          <div
            key={ic.id}
            style={{
              position: 'absolute',
              left: sx - cardW / 2,
              top: sy - 18,
              width: cardW,
              pointerEvents: 'none',
              zIndex: 20,
            }}
          >
            <div
              className={`rounded-lg border shadow-md flex flex-col overflow-hidden ${
                isNonDefault
                  ? darkMode
                    ? 'bg-amber-900/50 border-amber-600'
                    : 'bg-amber-50 border-amber-400'
                  : darkMode
                    ? 'bg-gray-800/90 border-gray-600'
                    : 'bg-white border-gray-200'
              }`}
            >
              {/* Top accent bar — full width, color-coded by type */}
              <div className="h-1 w-full flex-shrink-0" style={{ backgroundColor: typeColor }} />

              <div className="px-2 py-1.5 flex flex-col gap-1 min-w-0">
                {/* Row 1: type icon + label + non-default badge */}
                <div className="flex items-center gap-1 min-w-0">
                  <span
                    className="flex-shrink-0 font-mono font-bold"
                    style={{ fontSize: 9, color: typeColor }}
                  >
                    {TYPE_ICONS[ctrl.type] ?? '?'}
                  </span>
                  <span
                    className={`flex-1 truncate font-semibold leading-tight ${
                      darkMode ? 'text-gray-100' : 'text-gray-800'
                    }`}
                    style={{ fontSize: 9 }}
                  >
                    {ctrl.label}
                  </span>
                  {isNonDefault && (
                    <span
                      className="flex-shrink-0 px-1 py-0.5 rounded-full bg-amber-500 text-white font-bold leading-none"
                      style={{ fontSize: 6 }}
                    >
                      MOD
                    </span>
                  )}
                </div>

                {/* Row 2: control-type-specific value display */}
                {ctrl.type === ControlType.SLIDER && sliderPct !== null && (
                  <div className="flex flex-col gap-0.5">
                    {/* Segmented fill bar — 10 segments */}
                    <div className="flex gap-0.5">
                      {Array.from({ length: 10 }).map((_, i) => {
                        const filled = i / 10 < sliderPct;
                        const active = Math.floor(sliderPct * 10) === i;
                        return (
                          <div
                            key={i}
                            className="flex-1 rounded-sm"
                            style={{
                              height: 5,
                              backgroundColor: filled
                                ? isNonDefault ? '#f59e0b' : typeColor
                                : darkMode ? '#374151' : '#e5e7eb',
                              opacity: active ? 1 : filled ? 0.85 : 0.4,
                            }}
                          />
                        );
                      })}
                    </div>
                    {/* Min / value / max labels */}
                    <div className="flex justify-between items-center">
                      <span style={{ fontSize: 6 }} className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                        {ctrl.min ?? 0}
                      </span>
                      <span
                        className="font-bold tabular-nums"
                        style={{ fontSize: 8, color: isNonDefault ? '#f59e0b' : typeColor }}
                      >
                        {formatValue(ctrl)}
                      </span>
                      <span style={{ fontSize: 6 }} className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                        {ctrl.max ?? 1}
                      </span>
                    </div>
                  </div>
                )}

                {ctrl.type === ControlType.TOGGLE && (
                  <div className="flex items-center gap-1.5">
                    {/* Toggle track */}
                    <div
                      className="relative flex-shrink-0 rounded-full transition-colors"
                      style={{
                        width: 24,
                        height: 12,
                        backgroundColor: ctrl.current_value
                          ? isNonDefault ? '#f59e0b' : '#22c55e'
                          : darkMode ? '#374151' : '#d1d5db',
                      }}
                    >
                      <div
                        className="absolute top-0.5 rounded-full bg-white shadow-sm transition-all"
                        style={{
                          width: 8,
                          height: 8,
                          left: ctrl.current_value ? 14 : 2,
                        }}
                      />
                    </div>
                    <span
                      className="font-bold"
                      style={{
                        fontSize: 8,
                        color: ctrl.current_value
                          ? isNonDefault ? '#f59e0b' : '#22c55e'
                          : darkMode ? '#6b7280' : '#9ca3af',
                      }}
                    >
                      {ctrl.current_value ? 'ON' : 'OFF'}
                    </span>
                    {ctrl.current_value !== ctrl.default_value && (
                      <span style={{ fontSize: 7 }} className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                        (was {ctrl.default_value ? 'ON' : 'OFF'})
                      </span>
                    )}
                  </div>
                )}

                {ctrl.type === ControlType.SELECT && (
                  <div
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}
                  >
                    <span style={{ fontSize: 8, color: typeColor }}>▾</span>
                    <span
                      className="truncate font-medium"
                      style={{
                        fontSize: 8,
                        color: isNonDefault ? '#f59e0b' : darkMode ? '#d1d5db' : '#374151',
                      }}
                    >
                      {formatValue(ctrl)}
                    </span>
                  </div>
                )}

                {ctrl.type === ControlType.TEXT && (
                  <div
                    className="px-1.5 py-0.5 rounded border-l-2"
                    style={{ borderColor: isNonDefault ? '#f59e0b' : typeColor }}
                  >
                    <span
                      className="truncate block"
                      style={{
                        fontSize: 7,
                        color: isNonDefault ? '#f59e0b' : darkMode ? '#9ca3af' : '#6b7280',
                        fontStyle: 'italic',
                      }}
                    >
                      {formatValue(ctrl) || '(empty)'}
                    </span>
                  </div>
                )}

                {/* Row 3: description (truncated, 1 line) */}
                {ctrl.description && (
                  <p
                    className={`truncate leading-tight ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                    style={{ fontSize: 7 }}
                  >
                    {ctrl.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default ControlsClusterOverlay;
